/**
 * TicketTimeline Component
 *
 * Displays chronological activity history for a support ticket.
 * Shows notes, status changes, assignments, and other events.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 2: Ticket Detail Page Components
 * Location: apps/web/src/components/support/TicketTimeline.jsx
 */

import React from 'react';
import {
  MessageCircle,
  Clock,
  CheckCircle,
  Edit,
  Lock,
  Eye,
  Calendar,
  User,
  AlertCircle
} from 'lucide-react';
import { useTicketTimeline } from '../../hooks/support/useSupportTicket';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import EmptyState from '../common/EmptyState';

export function TicketTimeline({ ticketId }) {
  const { timeline, loading, error } = useTicketTimeline(ticketId);

  const getEventIcon = (_event) => {
    switch (event.event_type) {
      case 'note_added':
        return event.is_internal ? (
          <Lock className="w-5 h-5 text-gray-600" />
        ) : (
          <MessageCircle className="w-5 h-5 text-blue-600" />
        );
      case 'status_changed':
        if (event.new_value === 'resolved') {
          return <CheckCircle className="w-5 h-5 text-green-600" />;
        } else if (event.new_value === 'in_progress') {
          return <Clock className="w-5 h-5 text-blue-600" />;
        }
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'assigned':
        return <User className="w-5 h-5 text-purple-600" />;
      case 'priority_changed':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'ticket_updated':
        return <Edit className="w-5 h-5 text-gray-600" />;
      case 'ticket_created':
        return <Calendar className="w-5 h-5 text-gray-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getEventColor = (_event) => {
    switch (event.event_type) {
      case 'note_added':
        return event.is_internal ? 'border-gray-300' : 'border-blue-300';
      case 'status_changed':
        if (event.new_value === 'resolved') {return 'border-green-300';}
        if (event.new_value === 'in_progress') {return 'border-blue-300';}
        return 'border-yellow-300';
      case 'assigned':
        return 'border-purple-300';
      case 'priority_changed':
        return 'border-orange-300';
      case 'ticket_updated':
        return 'border-gray-300';
      default:
        return 'border-gray-200';
    }
  };

  const formatEventMessage = (_event) => {
    switch (event.event_type) {
      case 'note_added':
        return event.is_internal ? 'added an internal note' : 'sent a response';
      case 'status_changed':
        return `changed status from ${event.old_value} to ${event.new_value}`;
      case 'assigned':
        return event.old_value
          ? `reassigned ticket from ${event.old_value} to ${event.new_value}`
          : `assigned ticket to ${event.new_value}`;
      case 'priority_changed':
        return `changed priority from ${event.old_value} to ${event.new_value}`;
      case 'ticket_updated':
        return 'updated ticket details';
      case 'ticket_created':
        return 'created this ticket';
      default:
        return event.event_type.replace('_', ' ');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {return 'Just now';}
    if (diffMins < 60) {return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;}
    if (diffHours < 24) {return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;}
    if (diffDays < 7) {return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;}

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner message="Loading timeline..." size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="error" message={error} />
      </div>
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Clock}
          title="No activity yet"
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="space-y-6">
        {timeline.map((event, index) => (
          <div key={event.id} className="relative">
            {/* Timeline Line */}
            {index < timeline.length - 1 && (
              <div className="absolute left-5 top-10 bottom-0 w-px bg-gray-200"></div>
            )}

            <div className="flex gap-4">
              {/* Icon */}
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-full border-2 ${getEventColor(
                  event
                )} bg-white flex items-center justify-center`}
              >
                {getEventIcon(event)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Event Header */}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-semibold">{event.actor_name || 'System'}</span>{' '}
                      <span className="text-gray-600">{formatEventMessage(event)}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatTimestamp(event.created_at)}
                    </p>
                  </div>

                  {/* Internal Note Badge */}
                  {event.event_type === 'note_added' && event.is_internal && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-300 flex items-center gap-1 flex-shrink-0">
                      <Lock className="w-3 h-3" />
                      Internal
                    </span>
                  )}

                  {/* Public Response Badge */}
                  {event.event_type === 'note_added' && !event.is_internal && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-medium rounded-full border border-blue-300 flex items-center gap-1 flex-shrink-0">
                      <Eye className="w-3 h-3" />
                      Public
                    </span>
                  )}
                </div>

                {/* Event Content (for notes) */}
                {event.event_type === 'note_added' && event.content && (
                  <div
                    className={`mt-2 p-3 rounded-lg border ${
                      event.is_internal
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{event.content}</p>
                  </div>
                )}

                {/* Additional Details */}
                {event.metadata && Object.keys(event.metadata).length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    {Object.entries(event.metadata).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium">{key}:</span> {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TicketTimeline;
